<template lang="pug">
div.mapa-clientes-wrapper
  MapBaseLeaflet(
    ref="mapaBase"
    :marcadores="marcadoresFiltrados"
    :loading="loading"
    :sem-dados="semDados"
    :get-marker-color="corMarcador"
    @select-marker="marcadorSelecionado = $event"
    @clear-selection="marcadorSelecionado = null"
  )
  .map-title
    v-chip.d-none.d-sm-inline-flex(color="white", label)
      span.font-weight-bold {{ titulo }}
    v-chip(color="#BBDEFB", label)
      span.font-weight-bold Bases de Dados: {{ totalClientesUnicos }}
    v-chip(color="#C8E6C9", label)
      span.font-weight-bold Clientes: {{ totalEmpresasUnicas }}

  transition(name="fade")
    v-card.map-info-card(
      v-if="mostrarCardSelecionado"
      elevation="6"
      :style="{ borderLeftColor: corSoftwareHouse(marcadorSelecionado.softhouse) }"
    )
      .card-body
        .card-header
          .card-nomes
            .card-razao {{ marcadorSelecionado.razaoempresa || 'Não informado' }}
            .card-fantasia {{ marcadorSelecionado.fantasiaempresa || 'Não informado' }}
          v-btn(
            v-if="permitirEdicaoCoordenadas"
            class="card-action-btn"
            color="primary"
            outlined
            x-small
            @click="abrirDialogCoordenadas"
          )
            v-icon.card-action-icon(small, color="primary") mdi-crosshairs-gps
        .meta-row
          v-chip.softhouse-badge(
            small
            label
            dark
            :color="corSoftwareHouse(marcadorSelecionado.softhouse)"
          ) {{ marcadorSelecionado.softhouse || 'N/A' }}
          v-chip.version-badge(small, label, color="grey lighten-3")
            span.version-text {{ marcadorSelecionado.versao || 'N/A' }}
          .meta-divider
          v-chip.tipo-badge(small, label, outlined) {{ formatarTipo(marcadorSelecionado) }}

        v-divider.my-0

        .info-row
          span.info-text {{ formatarEndereco(marcadorSelecionado) }}

        v-divider.my-0

        .consultor-section
          .consultor-header
            .modulos-label.consultor-title CONSULTOR
            v-chip.consultor-badge(
              small
              label
              :style="{ backgroundColor: corBadgeConsultor(marcadorSelecionado.softhouse) }"
            )
              span.consultor-badge-text {{ marcadorSelecionado.consultor || 'Não informado' }}

        .modulos-section
          .modulos-label MÓDULOS
          .modulos-chips
            v-chip.modulo-chip(
              v-for="mod in modulosSelecionados"
              :key="mod"
              small
              label
              outlined
            ) {{ mod }}
            span.info-text(v-if="!modulosSelecionados.length") Não informado

      v-btn.card-close-btn.d-sm-none(icon, x-small, @click="limparSelecao")
        v-icon(small) mdi-close

  MapFilter.map-filter-overlay(
    ref="mapFilter"
    :filtros="configFiltros"
    :dados="marcadores"
    :disabled="semDados"
    @update:filtros="selecoesFiltro = $event"
  )

  MapSemLocalizacao.map-sem-localizacao-overlay(
    ref="mapSemLocalizacao"
    :dados="marcadoresSemLocalizacao"
    :total-bases-sem-localizacao="totalBasesSemLocalizacao"
    :total-clientes-sem-localizacao="totalClientesSemLocalizacao"
    :permitir-edicao-coordenadas="permitirEdicaoCoordenadas"
    :salvar-coordenadas-mapa-handler="salvarCoordenadasMapaHandler"
    @dialog-opened="onDialogOpened"
  )

  MapCoordenadasDialog(
    v-model="dialogCoordenadas"
    :item="itemDialogCoordenadas"
    :loading="salvandoCoordenadas"
    @save="salvarCoordenadas"
  )
</template>

<script>
import MapBaseLeaflet from "./MapBaseLeaflet.vue";
import MapCoordenadasDialog from "./MapCoordenadasDialog.vue";
import MapFilter from "./MapFilter.vue";
import MapSemLocalizacao from "./MapSemLocalizacao.vue";
import { CORES_SISTEMA } from "./mapConfig";

export default {
  name: "MapaClientesMap",

  components: {
    MapBaseLeaflet,
    MapCoordenadasDialog,
    MapFilter,
    MapSemLocalizacao
  },

  props: {
    marcadores: {
      type: Array,
      default: () => []
    },
    marcadoresSemLocalizacao: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    semDados: {
      type: Boolean,
      default: false
    },
    titulo: {
      type: String,
      default: ""
    },
    configFiltros: {
      type: Array,
      default: () => []
    },
    salvarCoordenadasMapaHandler: {
      type: Function,
      default: null
    },
    permitirEdicaoCoordenadas: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      selecoesFiltro: {},
      marcadorSelecionado: null,
      itemDialogCoordenadas: null,
      dialogCoordenadas: false,
      salvandoCoordenadas: false
    };
  },

  computed: {
    totalClientesUnicos() {
      return new Set(this.marcadoresFiltrados.map(m => m.codigocliente)).size;
    },

    totalBasesSemLocalizacao() {
      const basesComLocalizacao = new Set(this.marcadores.map(m => m.codigocliente));

      return new Set(
        this.marcadoresSemLocalizacao
          .filter(m => !basesComLocalizacao.has(m.codigocliente))
          .map(m => m.codigocliente)
      ).size;
    },

    totalClientesSemLocalizacao() {
      return new Set(
        this.marcadoresSemLocalizacao.map(m => `${m.codigocliente}_${m.codigoempresa}`)
      ).size;
    },

    totalEmpresasUnicas() {
      return new Set(this.marcadoresFiltrados.map(m => `${m.codigocliente}_${m.codigoempresa}`))
        .size;
    },

    marcadoresFiltrados() {
      let resultado = this.marcadores;
      for (const filtro of this.configFiltros) {
        const valor = this.selecoesFiltro[filtro.campo];
        if (valor && valor.length > 0) {
          resultado = resultado.filter(marcador => valor.includes(marcador[filtro.campo]));
        }
      }
      return resultado;
    },

    modulosSelecionados() {
      return this.formatarModulos(this.marcadorSelecionado);
    },

    mostrarCardSelecionado() {
      return !!this.marcadorSelecionado;
    }
  },

  watch: {
    marcadoresFiltrados() {
      if (!this.marcadorSelecionado) return;

      const aindaExiste = this.marcadoresFiltrados.includes(this.marcadorSelecionado);
      if (!aindaExiste) {
        this.limparSelecao();
      }
    },

    dialogCoordenadas(valor) {
      if (!valor) {
        this.itemDialogCoordenadas = null;
      }
    }
  },

  methods: {
    abrirDialogCoordenadas() {
      if (!this.marcadorSelecionado) return;

      if (this.isMobile()) {
        this.fecharOverlays();
      }

      this.itemDialogCoordenadas = this.marcadorSelecionado;
      this.dialogCoordenadas = true;

      if (this.isMobile()) {
        this.limparSelecao();
      }
    },

    fecharOverlays() {
      if (this.$refs.mapFilter) {
        this.$refs.mapFilter.fecharPainel();
      }
      if (this.$refs.mapSemLocalizacao) {
        this.$refs.mapSemLocalizacao.fecharPainel();
      }
    },

    onDialogOpened() {
      if (this.isMobile()) {
        this.fecharOverlays();
      }
    },

    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    corMarcador(marcador) {
      return this.corSoftwareHouse(marcador.softhouse);
    },

    corSoftwareHouse(softhouse) {
      return CORES_SISTEMA[softhouse] || "#757575";
    },

    corBadgeConsultor(softhouse) {
      const nome = String(softhouse || "").toLowerCase();
      if (nome.includes("controlsoft")) return "rgba(5, 148, 103, 0.2)";
      if (nome.includes("sysafra")) return "rgba(10, 90, 219, 0.2)";
      return "rgba(0, 0, 0, 0.07)";
    },

    formatarEndereco(marcador) {
      const partes = [
        marcador.endereco,
        marcador.bairro,
        marcador.cep,
        marcador.nomecidade,
        marcador.uf
      ].filter(Boolean);
      return partes.length ? partes.join(", ") : "Não informado";
    },

    formatarTipo(marcador) {
      return marcador.tipoempresa || "Não informado";
    },

    formatarModulos(marcador) {
      if (!marcador || !marcador.modulos) return [];
      try {
        const parsed =
          typeof marcador.modulos === "string" ? JSON.parse(marcador.modulos) : marcador.modulos;
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    },

    async salvarCoordenadas(coordenadas) {
      if (!this.itemDialogCoordenadas || !this.salvarCoordenadasMapaHandler) return;

      this.salvandoCoordenadas = true;
      try {
        await this.salvarCoordenadasMapaHandler({
          item: this.itemDialogCoordenadas,
          coordenadas
        });
        this.dialogCoordenadas = false;
      } finally {
        this.salvandoCoordenadas = false;
      }
    },

    limparSelecao() {
      this.marcadorSelecionado = null;
      if (this.$refs.mapaBase) {
        this.$refs.mapaBase.deselecionarMarcador();
      }
    }
  }
};
</script>

<style scoped>
.mapa-clientes-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.map-title {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1000;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  max-width: calc(100% - 20px);
  gap: 4px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.map-info-card {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 150;
  width: min(400px, calc(100vw - 20px));
  border-radius: 10px;
  border-left: 4px solid;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.45), 0 2px 8px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

.card-body {
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.card-razao {
  font-size: 14px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.4;
  letter-spacing: -0.01em;
}

.card-fantasia {
  font-size: 12px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.5);
  line-height: 1.4;
  margin-top: 2px;
  text-transform: uppercase;
}

.card-close-btn {
  position: absolute;
  bottom: 8px;
  right: 8px;
  z-index: 1;
}

.meta-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.softhouse-badge {
  font-weight: 500 !important;
  letter-spacing: 0.02em;
}

.version-badge {
  font-weight: 700 !important;
}

.version-text {
  font-family: monospace;
  font-weight: 700;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.9);
}

.meta-divider {
  width: 1px;
  height: 18px;
  background: rgba(0, 0, 0, 0.2);
}

.tipo-badge {
  font-weight: 600 !important;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.info-text {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.9);
  line-height: 1.55;
}

.consultor-section {
  margin-top: 1px;
}

.consultor-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  min-height: 20px;
}

.consultor-title {
  display: inline-flex;
  align-items: center;
  line-height: 1;
}

.consultor-badge {
  max-width: 100%;
  height: auto !important;
}

.consultor-badge-text {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
}

.modulos-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.modulos-label {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1;
}

.modulos-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.modulo-chip {
  font-weight: 500 !important;
}

.card-action-btn {
  min-width: 30px !important;
  width: 30px !important;
  max-width: 30px !important;
  min-height: 30px !important;
  height: 30px !important;
  max-height: 30px !important;
  padding: 0 !important;
  border-radius: 4px;
}

.card-action-icon {
  font-size: 15px !important;
}

::v-deep .card-action-btn .v-btn__content {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.map-filter-overlay {
  z-index: 1000;
}

.map-sem-localizacao-overlay {
  z-index: 1000;
}
</style>
